import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { RefreshCw, Camera } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AvatarCropDialog } from '@/components/settings/AvatarCropDialog';
import { useToast } from '@/hooks/use-toast';
import { getProxiedImageUrl } from '@/utils/imageProxy';

interface CreatorAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreatorAdded: () => void;
}

const PLATFORMS = ['tiktok', 'instagram', 'youtube'] as const;

const platformColors: Record<string, string> = {
  tiktok: 'bg-[#00f2ea]/10 text-[#00f2ea]',
  instagram: 'bg-[#fd1d1d]/10 text-[#fd1d1d]',
  youtube: 'bg-[#ff0000]/10 text-[#ff0000]',
};

export const CreatorAddDialog: React.FC<CreatorAddDialogProps> = ({ open, onOpenChange, onCreatorAdded }) => {
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usernames, setUsernames] = useState<Record<string, string>>({ tiktok: '', instagram: '', youtube: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  // Track uploaded avatar blob for saving later
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);

  const resetForm = () => {
    setDisplayName('');
    setAvatarUrl('');
    setAvatarBlob(null);
    setUsernames({ tiktok: '', instagram: '', youtube: '' });
  };

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = async (blob: Blob) => {
    setIsCropOpen(false);
    setCropImageSrc(null);
    // Preview locally, store blob for upload on save
    const previewUrl = URL.createObjectURL(blob);
    setAvatarUrl(previewUrl);
    setAvatarBlob(blob);
  };

  const uploadAvatar = async (id: string): Promise<string | null> => {
    if (!avatarBlob) return null;
    try {
      const filePath = `creators/${id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, avatarBlob, { contentType: 'image/jpeg', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
      return publicUrl;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    const filledPlatforms = PLATFORMS.filter(p => usernames[p]?.trim());
    if (filledPlatforms.length === 0) {
      toast({ title: 'Validation', description: 'Enter at least one platform username.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const primaryUsername = usernames[filledPlatforms[0]]!.trim().replace(/^@/, '');
      const name = displayName.trim() || primaryUsername;

      if (filledPlatforms.length === 1) {
        // Single platform — insert creator directly
        const { data: newCreator, error } = await supabase.from('creators').insert({
          platform: filledPlatforms[0],
          username: primaryUsername,
          platform_id: primaryUsername,
          display_name: displayName.trim() || null,
          is_linked: false,
        }).select().single();
        if (error) throw error;

        // Upload avatar if provided
        if (avatarBlob && newCreator) {
          const url = await uploadAvatar(newCreator.id);
          if (url) {
            await supabase.from('creators').update({ avatar_url: url }).eq('id', newCreator.id);
          }
        }
      } else {
        // Multiple platforms — create group first
        const { data: newGroup, error: groupErr } = await supabase.from('creator_groups').insert({
          primary_name: name,
          display_name: displayName.trim() || null,
          total_followers: 0,
        }).select().single();
        if (groupErr) throw groupErr;

        // Upload avatar to group
        if (avatarBlob && newGroup) {
          const url = await uploadAvatar(newGroup.id);
          if (url) {
            await supabase.from('creator_groups').update({ avatar_url: url }).eq('id', newGroup.id);
          }
        }

        // Insert all creator accounts linked to group
        for (const platform of filledPlatforms) {
          const username = usernames[platform]!.trim().replace(/^@/, '');
          const { error } = await supabase.from('creators').insert({
            platform,
            username,
            platform_id: username,
            creator_group_id: newGroup.id,
            display_name: displayName.trim() || null,
            is_linked: false,
          });
          if (error) throw error;
        }
      }

      toast({ title: 'Creator Added', description: `${filledPlatforms.length} account(s) added. Use Link to enrich.` });
      onCreatorAdded();
      resetForm();
      onOpenChange(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to add creator.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
        <DialogContent className="sm:max-w-[540px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Creator</DialogTitle>
            <DialogDescription>
              Add a new creator with one or more social accounts
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Avatar */}
            <div className="flex justify-center">
              <div className="relative">
                <Avatar className="h-24 w-24 border-2 border-border">
                  {avatarUrl && (
                    <AvatarImage src={avatarUrl.startsWith('blob:') ? avatarUrl : getProxiedImageUrl(avatarUrl)} className="object-cover" />
                  )}
                  <AvatarFallback className="text-2xl font-semibold bg-muted">
                    {(displayName || '?').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarFileChange}
                />
              </div>
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Creator display name"
              />
            </div>

            {/* Social Accounts */}
            <div className="space-y-3">
              <Label>Social Accounts</Label>
              {PLATFORMS.map((platform) => (
                <div key={platform} className="p-3 rounded-lg border border-dashed border-border bg-muted/10 flex items-center gap-3">
                  <Badge variant="secondary" className={`text-xs ${platformColors[platform]}`}>
                    {platform}
                  </Badge>
                  <div className="flex-1">
                    <Input
                      value={usernames[platform] || ''}
                      onChange={(e) => setUsernames(prev => ({ ...prev, [platform]: e.target.value }))}
                      placeholder="username"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cropImageSrc && (
        <AvatarCropDialog
          open={isCropOpen}
          imageSrc={cropImageSrc}
          onConfirm={handleCropConfirm}
          onCancel={() => { setIsCropOpen(false); setCropImageSrc(null); }}
        />
      )}
    </>
  );
};
